import type { PlanFeature } from '../types/plans';
import type { PlanType } from '../types/payment';
import {
  PLAN_LIMITS,
  PLAN_PRICING,
  PLAN_CREDITS,
  formatLimitText,
} from '../config/planConfig';

// Base plan structure
export interface UnifiedPlan {
  id: PlanType | 'free';
  name: string;
  description: string;

  // Pricing information
  monthlyPrice: number;
  yearlyPrice: number;

  // Feature information
  features: PlanFeature[];

  // Payment-specific data
  credits?: number;

  buttonText: string;
  isRecommended?: boolean;
  isPopular?: boolean;
  isFree?: boolean;
  // Price Plan v2
  isComingSoon?: boolean;
  upgradeEntry?: boolean;
  // Price-description update (YEP-588): daily-price framing + per-surface copy.
  /** Approximate daily price, e.g. 9.9 → "≈ $9.9 / day". */
  dailyPrice?: number;
  /** Small grey note under the description (e.g. equivalent-human-team line). */
  taglineNote?: string;
  /** Description shown on the in-app Subscription & billing page. */
  billingDescription?: string;
  /** Button opens the sales Calendly instead of the payment flow. */
  isContactSales?: boolean;
}

export const UNIFIED_PLANS: UnifiedPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'For solo operators and small teams',
    monthlyPrice: PLAN_PRICING.starter.monthlyPrice,
    yearlyPrice: PLAN_PRICING.starter.yearlyPrice,
    buttonText: 'Start 14-day free trial',
    credits: PLAN_CREDITS.starter,
    isRecommended: false,
    features: [
      { text: formatLimitText(PLAN_LIMITS.starter.conversationsPerMonth, 'conversations / month'), included: true },
      { text: formatLimitText(PLAN_LIMITS.starter.productsTraining, 'products training'), included: true },
      { text: 'Sales skills', included: true },
      { text: `${PLAN_LIMITS.starter.chatHistoryDays}-day chat history`, included: true },
      { text: 'Single agent', included: true },
      { text: 'Talking AI avatar', included: true },
      { text: 'Fully-customizable chatbox', included: true },
      { text: 'Real-time product information updates', included: true },
    ],
  },
  {
    id: 'basic',
    name: 'Basic',
    description: 'For growing stores looking for more scalability',
    monthlyPrice: PLAN_PRICING.basic.monthlyPrice,
    yearlyPrice: PLAN_PRICING.basic.yearlyPrice,
    buttonText: 'Start 14-day free trial',
    credits: PLAN_CREDITS.basic,
    isRecommended: true,
    features: [
      { text: formatLimitText(PLAN_LIMITS.basic.conversationsPerMonth, 'conversations / month'), included: true },
      { text: formatLimitText(PLAN_LIMITS.basic.productsTraining, 'products training'), included: true },
      { text: 'Sales skills', included: true },
      { text: `${PLAN_LIMITS.basic.chatHistoryDays}-day chat history`, included: true },
      { text: 'Multiple agents', included: true },
      { text: 'Talking AI avatar', included: true },
      { text: 'Fully-customizable chatbox', included: true },
      { text: 'Real-time product information updates', included: true },
    ],
  },
  {
    id: 'startup',
    name: 'Pro',
    description: 'For growing merchants & mid-size businesses',
    monthlyPrice: PLAN_PRICING.startup.monthlyPrice,
    yearlyPrice: PLAN_PRICING.startup.yearlyPrice,
    buttonText: 'Start 14-day free trial',
    credits: PLAN_CREDITS.startup,
    isRecommended: false,
    features: [
      { text: formatLimitText(PLAN_LIMITS.startup.conversationsPerMonth, 'conversations / month'), included: true },
      { text: formatLimitText(PLAN_LIMITS.startup.productsTraining, 'products training'), included: true },
      { text: 'Sales skills', included: true },
      { text: `${PLAN_LIMITS.startup.chatHistoryDays}-day chat history`, included: true },
      { text: 'Multiple agents', included: true },
      { text: 'Talking AI avatar', included: true },
      { text: 'Fully-customizable chatbox', included: true },
      { text: 'Real-time product information updates', included: true },
    ],
  },
  // Price Plan v2 — NEW cohort plans. Business is the primary offer ("AI Team");
  // Premium is shown but Coming Soon (not yet subscribable).
  {
    id: 'business',
    name: 'Business',
    description: 'Your AI Team — access to 2 AI Employees, working 24/7',
    monthlyPrice: PLAN_PRICING.business.monthlyPrice,
    yearlyPrice: PLAN_PRICING.business.yearlyPrice,
    buttonText: 'Start 14-day free trial',
    credits: PLAN_CREDITS.business,
    isRecommended: true,
    features: [
      { text: 'Access to 2 AI Employees', included: true },
      { text: '24/7 availability', included: true },
      { text: 'Multi-step automation', included: true },
      { text: 'Branded AI avatar', included: true },
      { text: 'Unlimited conversations / chat history', included: true },
      { text: 'Unlimited products training', included: true },
      { text: 'Fully-customizable chatbox', included: true },
      { text: 'Real-time product information updates', included: true },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'All AI Employees, advanced automation',
    monthlyPrice: PLAN_PRICING.premium.monthlyPrice,
    yearlyPrice: PLAN_PRICING.premium.yearlyPrice,
    buttonText: 'Coming Soon',
    credits: PLAN_CREDITS.premium,
    isRecommended: false,
    isComingSoon: true,
    features: [
      { text: 'Access to all AI Employees', included: true },
      { text: '24/7 availability', included: true },
      { text: 'Advanced multi-step automation', included: true },
      { text: 'Branded AI avatar', included: true },
      { text: 'Unlimited conversations / chat history', included: true },
      { text: 'Unlimited products training', included: true },
      { text: 'Fully-customizable chatbox', included: true },
      { text: 'Real-time product information updates', included: true },
    ],
  },
  // Digital Staff credit plans (Core / Growth) — sold via Stripe on the
  // onboarding plan page + in-app billing. Text mirrors planTiers.ts.
  {
    id: 'ai_starter',
    name: 'Core',
    description:
      'Get a full-time AI marketing specialist for your ecommerce store for just $6.6 per day.',
    monthlyPrice: PLAN_PRICING.ai_starter.monthlyPrice,
    yearlyPrice: PLAN_PRICING.ai_starter.yearlyPrice,
    buttonText: 'Start 14-day free trial',
    credits: PLAN_CREDITS.ai_starter,
    isRecommended: false,
    features: [
      { text: '4,000 AI Credits per Month', included: true },
      { text: 'AI Campaign Brief Generation', included: true },
      { text: 'Website Blog Content Creation', included: true },
      { text: 'Weekly Social Media Content', included: true },
      { text: 'Product Description Generation', included: true },
      { text: 'Shopify Publishing Support', included: true },
      { text: 'Basic Brand Voice Training', included: true },
      { text: 'Basic Performance Reporting', included: true },
      { text: 'Self-Serve Onboarding', included: true },
      { text: 'Cancel Anytime — No Lock-In Contract', included: true },
      { text: '14-Day Free Trial', included: true },
    ],
  },
  {
    id: 'ai_growth',
    name: 'Growth',
    description:
      'Get 2 full-time AI employees for marketing and customer support for just $9.9 per day.',
    monthlyPrice: PLAN_PRICING.ai_growth.monthlyPrice,
    yearlyPrice: PLAN_PRICING.ai_growth.yearlyPrice,
    buttonText: 'Start 14-day free trial',
    credits: PLAN_CREDITS.ai_growth,
    isRecommended: true,
    isPopular: true,
    features: [
      { text: '7,000 AI Credits per Month', included: true },
      { text: '24/7 Shopify Website Chat', included: true },
      { text: 'AI-Powered Pre-Sale Customer Support', included: true },
      { text: 'Smart Product Recommendations', included: true },
      { text: 'Upsell & Cross-Sell Support', included: true },
      { text: 'Abandoned Cart Recovery', included: true },
      { text: 'Order Status & Tracking', included: true },
      { text: 'Support for 90+ Languages', included: true },
      { text: 'Standard Onboarding', included: true },
      { text: 'Cancel Anytime — No Lock-In Contract', included: true },
      { text: '14-Day Free Trial', included: true },
    ],
  },
];

export const FREE_PLAN: UnifiedPlan = {
  id: 'free',
  name: 'Free',
  description: 'Perfect for small businesses and solo operators',
  monthlyPrice: PLAN_PRICING.free.monthlyPrice,
  yearlyPrice: PLAN_PRICING.free.yearlyPrice,
  buttonText: 'Continue as free',
  isFree: true,
  features: [
    { text: formatLimitText(PLAN_LIMITS.free.conversationsPerMonth, 'conversations / month'), included: true },
    { text: formatLimitText(PLAN_LIMITS.free.productsTraining, 'products training'), included: true },
    { text: 'Sales skills', included: true },
    { text: `${PLAN_LIMITS.free.chatHistoryDays}-day chat history`, included: true },
    { text: 'Email support', included: true },
    { text: 'Detailed analytics', included: true },
    { text: 'Real-time product information updates', included: true },
  ],
};

export const ALL_PLANS = [...UNIFIED_PLANS, FREE_PLAN];
